
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import { amountFormatter } from '../../../utility/utils/formatter';
import { formatDateMDYYYY } from '../../../utility/utils/dateUtil';
import Globals, { URLS } from '../../../utility/constants/appConstants';

import InvoicesPopUp from '../../ui/InvoicesPopUp';
import Storage from '../../../utility/features/helpers/storageHelper';
import { encryptData } from '../../../utility/utils/hashing';

import { Collapse } from 'bootstrap';

const ServiceInvoices = ({ labels, invoices, id, serviceDetailsCss }) => {
  const [popupData, setPopupData] = useState({});
  const modalRef = useRef(null);
  const modalInstanceRef = useRef(null); // Bootstrap 5 Modal instance if available
  const backdropRef = useRef(null);      // Fallback backdrop element
  const modalOpenRef = useRef(false);    // Track modal open state

  const collapseId = `ServiceInvoices${id}`;
  const collapseInstRef = useRef(null);
  const contentRef = useRef(null);
  const [open, setOpen] = useState(false);
  const customerSummary = useSelector((state) => state.login.customerInfo);
  const custNumberStore = customerSummary?customerSummary?.[0]?.CustomerNumber:"";
  const encryptedCustNo = custNumberStore ? encryptData(`${custNumberStore}__customervalue${Date.now()}`, "customerNumber") : '';
  const formatedCustomerNumber = encryptedCustNo.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

  useEffect(() => {
    if (!contentRef.current) return;
    // Create or get instance, and prevent auto-toggling at init
    collapseInstRef.current = Collapse.getOrCreateInstance(contentRef.current, { toggle: false });
  }, []);

  useEffect(() => {
    const inst = collapseInstRef.current;
    if (!inst) return;
    open ? inst.show() : inst.hide();
  }, [open]);

  // Bootstrap 5 native modal setup (no jQuery)
  const ensureModalInstance = useCallback(() => {
    if (typeof window === 'undefined') return;
    const bootstrap = window.bootstrap;
    if (bootstrap?.Modal && modalRef.current && !modalInstanceRef.current) {
      modalInstanceRef.current = new bootstrap.Modal(modalRef.current, {
        backdrop: 'static',
      });
    }
  }, []);

  const showModalFallback = useCallback(() => {
    if (!modalRef.current) return;
    const el = modalRef.current;
    el.classList.add('show');
    el.style.display = 'block';
    el.removeAttribute('aria-hidden');
    el.setAttribute('aria-modal', 'true');

    // Create backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop fade show';
    document.body.appendChild(backdrop);
    backdropRef.current = backdrop;

    document.body.classList.add('modal-open');
  }, []);

  const hideModalFallback = useCallback(() => {
    if (!modalRef.current) return;
    const el = modalRef.current;
    el.classList.remove('show');
    el.style.display = '';
    el.setAttribute('aria-hidden', 'true');
    el.removeAttribute('aria-modal');

    // Remove backdrop
    if (backdropRef.current && backdropRef.current.parentNode) {
      backdropRef.current.parentNode.removeChild(backdropRef.current);
      backdropRef.current = null;
    }
    document.body.classList.remove('modal-open');
  }, []);

  // Show the modal
  const serviceModal = useCallback(
    (e, dataForPopup) => {
      e.preventDefault();

      // analytics
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'account_action',
        category: 'Account',
        action: 'service details',
        label: 'view invoice',
        customer_id: custNumberStore,
        error: { reason: '' },
      });

      setPopupData(dataForPopup);

      // Push a history state when opening modal to handle back button
      if (!modalOpenRef.current) {
        window.history.pushState({ modalOpen: true }, '');
        modalOpenRef.current = true;
      }

      // Show using Bootstrap 5 if available, else fallback
      ensureModalInstance();
      if (modalInstanceRef.current) {
        modalInstanceRef.current.show();
      } else {
        showModalFallback();
      }
    },
    [ensureModalInstance, showModalFallback]
  );

  // Hide the modal
  const handleClose = useCallback(
    (e) => {
      if (e) e.preventDefault();
      
      if (modalInstanceRef.current) {
        modalInstanceRef.current.hide();
      } else {
        hideModalFallback();
      }

      // Remove history state if modal was opened with history
      if (modalOpenRef.current) {
        modalOpenRef.current = false;
        // Only go back if the current state is our modal state
        if (window.history.state?.modalOpen) {
          window.history.back();
        }
      }
    },
    [hideModalFallback]
  );

  // Clean up on unmount: dispose bootstrap instance and fallback backdrop
  useEffect(() => {
    return () => {
      try {
        modalInstanceRef.current?.hide();
        modalInstanceRef.current?.dispose?.();
      } catch (_) {}
      if (backdropRef.current) {
        hideModalFallback();
      }
    };
  }, [hideModalFallback]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      // If modal is open and user pressed back, close it
      if (modalOpenRef.current) {
        modalOpenRef.current = false;
        if (modalInstanceRef.current) {
          modalInstanceRef.current.hide();
        } else {
          hideModalFallback();
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hideModalFallback]);

  // Desktop rows
  const desktopRows = useMemo(
    () =>
      invoices.map((invoice, i) => {
        let dollars = `${Globals.currencySymbol || '$'}${amountFormatter(invoice.invoiceAmount).dollars}`;
        let cents = ` ${amountFormatter(invoice.invoiceAmount).cents}`;
        let keyword = invoice.invoiceOpen === 'N' ? 'Yes' : 'No';
        const encryptedWONo = custNumberStore && invoice.workOrderRef ? encryptData(invoice.workOrderRef + `__documentvalue${Date.now()}`, "serviceWorkOrder") : "";

        const popData = {
          invoiceNumber: invoice.invoiceNum,
          invAmtIntPart: dollars,
          invAmtFractPart: cents,
          invoiceOpen: invoice.invoiceOpen,
          invoiceDate: formatDateMDYYYY(invoice.invoiceDate),
          date: formatDateMDYYYY(invoice.workOrderSvcDate),
          desc: invoice.svcDescription,
          ticketLink: custNumberStore && invoice.workOrderRef ? `${process.env.REACT_APP_CMS_URL}/document/commercial/${formatedCustomerNumber}/workordernumber/${encryptedWONo}` : ''
        };

        let invoiceNum = (
          <a
            href="#"
            onClick={(e) => serviceModal(e, popData)}
            className="view-invoice g-text-underline"
            data-invoice={invoice.invoiceNum}
          >
            #{invoice.invoiceNum}
          </a>
        );

        let svcReserviceFlag = 'Regular';
        if (invoice.svcReserviceFlag === 'Y') {
          dollars = ' ';
          cents = ' ';
          keyword = ' ';
          invoiceNum = ' ';
          svcReserviceFlag = 'ReService';
        }

        return (
          <tr key={`soln${i}`}>
            <td>{invoice.svcDescription}</td>
            <td>{formatDateMDYYYY(invoice.workOrderSvcDate)}</td>
            <td>{invoiceNum}</td>
            <td>
              <a
                href={custNumberStore && invoice.workOrderRef ? `${process.env.REACT_APP_CMS_URL}/document/commercial/${formatedCustomerNumber}/workordernumber/${encryptedWONo}` : ''}
                className="g-text-underline view-service-ticket"
                data-ticket-id={invoice.workOrderRef}
                rel="noopener noreferrer"
                target="_blank"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: 'account_action',
                    category: 'Account',
                    action: 'service details',
                    label: 'view service ticket',
                    customer_id: custNumberStore,
                    error: { reason: '' },
                  });
                }}
              >
                {labels.view}
              </a>
            </td>
            <td>{svcReserviceFlag}</td>
            <td>
              {dollars}
              <sup>{cents}</sup>
            </td>
            <td>{keyword}</td>
          </tr>
        );
      }),
    [invoices, labels.view, serviceModal]
  );

  // Mobile rows (object representation passed to MobileServiceInvoices)
  const mobileRows = useMemo(
    () =>
      invoices.map((invoice, i) => {
        let dollars = `${Globals.currencySymbol}${amountFormatter(invoice.invoiceAmount).dollars}`;
        let cents = ` ${amountFormatter(invoice.invoiceAmount).cents}`;
        let keyword = invoice.invoiceOpen === 'N' ? 'Yes' : 'No';
        const encryptedWONo = custNumberStore && invoice.workOrderRef ? encryptData(invoice.workOrderRef + `__documentvalue${Date.now()}`, "serviceWorkOrder") : "";

        const popData = {
          invoiceNumber: invoice.invoiceNum,
          invAmtIntPart: dollars,
          invAmtFractPart: cents,
          invoiceOpen: invoice.invoiceOpen,
          date: formatDateMDYYYY(invoice.workOrderSvcDate),
          desc: invoice.svcDescription,
          ticketLink: custNumberStore && invoice.workOrderRef ? `${process.env.REACT_APP_CMS_URL}/document/commercial/${formatedCustomerNumber}/workordernumber/${encryptedWONo}` : ''
        };

        let invoiceNum = (
          <a
            href="#"
            onClick={(e) => serviceModal(e, popData)}
            className="view-invoice g-text-underline"
            data-invoice={invoice.invoiceNum}
          >
            #{invoice.invoiceNum}
          </a>
        );

        let svcReserviceFlag = 'Regular';
        if (invoice.svcReserviceFlag === 'Y') {
          dollars = ' ';
          cents = ' ';
          keyword = ' ';
          invoiceNum = ' ';
          svcReserviceFlag = 'ReService';
        }

        return {
          key: `soln${i}`,
          svcDescription: invoice.svcDescription,
          workOrderSvcDate: formatDateMDYYYY(invoice.workOrderSvcDate),
          invoiceNum: invoice.invoiceNum,
          popUp: invoiceNum,
          href: custNumberStore && invoice.workOrderRef ? `${process.env.REACT_APP_CMS_URL}/document/commercial/${formatedCustomerNumber}/workordernumber/${encryptedWONo}` : '',
          workOrderRef: invoice.workOrderRef,
          svcReserviceFlag,
          dollars,
          cents,
          keyword,
        };
      }),
    [invoices, serviceModal]
  );

  return (
    <>
      <div id="invoice_accordian" className="row">
        <div className={`bg-dark col-12 ${serviceDetailsCss['view-invoice-detail']}`}>
          <a
            className={`d-block py-10 text-decoration-none text-white ${open?"":"collapsed"}`}
            role="button"
            aria-expanded={open}
            aria-controls={collapseId}
            onClick={() => setOpen((prev) => !prev)}
          >
            <div className="align-items-center row">
              <div className={`col-10 ${serviceDetailsCss['invoice-toggle']}`}>{labels.viewInvoices}</div>
              <div className="col-2 glh-1 text-center">
                <img
                  src="/images/MyAccount/RightArrow.svg"
                  alt="View Service Invoices"
                  className={`img-fluid invoice-arrow ${serviceDetailsCss['arror-toggle-icon']}`}
                  style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .25s ease' }}
                />
              </div>
            </div>
          </a>
        </div>

        <div className={`bg-white w-100 p-0`}>
          <div
            className={`collapse ${serviceDetailsCss['tru-mma-main-service-box']} `}
            ref={contentRef}
            id={collapseId}
          >
            {mobileRows.map((invoice, i) => (
              <MobileServiceInvoices invoice={invoice} labels={labels} key={i} serviceDetailsCss={serviceDetailsCss} />
            ))}

            <div className="card card-body d-none d-md-block p-20 p-lg-30 rounded-0">
              <table className={`gfs-12 ${serviceDetailsCss['gfs-lg-16']} m-0 table table-borderless table-striped`}>
                <thead>
                  <tr>
                    <th scope="col">{labels.serviceDescription}</th>
                    <th scope="col">{labels.dateOfService}</th>
                    <th scope="col">{labels.invoice}</th>
                    <th scope="col">{labels.serviceTicket}</th>
                    <th scope="col">{labels.serviceType}</th>
                    <th scope="col">{labels.amount}</th>
                    <th scope="col">{labels.paid}</th>
                  </tr>
                </thead>
                <tbody>{desktopRows}</tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
            
      {/* The modal itself is rendered by InvoicesPopUp; we pass the ref and close handler */}
      <InvoicesPopUp
        modalRef={modalRef}
        labels={labels}
        handleCloseModal={handleClose}
        data={popupData}
        serviceDetailsCss={serviceDetailsCss}
      />
    </>
  );
};

const MobileServiceInvoices = ({ labels, invoice, serviceDetailsCss }) => {
  return (
    <>
      <div className={`card card-body d-md-none gfs-18 ${serviceDetailsCss['gfw-3']} ${serviceDetailsCss['mob-invoice-box']} pb-20 pt-30 px-16`}>
        <div>
          <span className="d-block">{invoice.svcDescription}</span>

          <span className="d-block">
            {labels.date}
            {invoice.workOrderSvcDate}
          </span>

          <span className="d-block">
            {labels.invoice + ': '}
            {/* This is provided by the parent and already wires up the modal */}
            {invoice.popUp}
          </span>

          <span className="d-block">
            {labels.serviceTicket}:
            <a
              href={invoice.href}
              className="g-text-underline view-service-ticket"
              data-ticket-id={invoice.workOrderRef}
              rel="noopener noreferrer"
              target="_blank"
            >
              {labels.view}
            </a>
          </span>

          <span className="d-block">
            {labels.serviceType}: {invoice.svcReserviceFlag}
          </span>

          <span className="d-block">
            {labels.amount}:{invoice.dollars}
            <sup>{invoice.cents}</sup>
          </span>

          <span className="d-block">
            {labels.paid}:{invoice.keyword}
          </span>

          <hr className="my-10" />
        </div>
      </div>
    </>
  );
};

export default ServiceInvoices;